import React, {useContext, useEffect, useMemo, useState} from "react";
import Markdown, {Components, defaultUrlTransform} from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import {CodeBlock, renderNode} from "./blocks/CodeBlock";
import {ChartBlock, ChartSkeleton, ChartUnavailable, isChartNode, looksLikeChartFence} from "./blocks/ChartBlock";
import {CompareBlock, CompareSkeleton, CompareUnavailable, isCompareNode, looksLikeCompareFence, looksLikeCompareJson} from "./blocks/CompareBlock";
import {EmailBlock} from "./blocks/EmailBlock";
import {deduplicateEmailFences} from "./blocks/selectedEmail";
import {ResourcesBlock} from "./blocks/ResourcesBlock";
import {DetailsBlock, DetailsSkeleton, DetailsUnavailable, parseResolvedInfoBlock, StatsBlock, StatsSkeleton, StatsUnavailable} from "./blocks/InfoBlock";
import {useAnimatedText} from "../../hooks";
import {Citation} from "./inline/Citation";
import {CompanyReference} from "./inline/CompanyReference";
import {ActionIcon, Anchor, Box, Modal, Stack, Table, Text} from "@mantine/core";
import classes from "./GPTStreamParser.module.css";
import {useDebouncedValue} from "@mantine/hooks";
import {
    processReferences,
    stripLCitations,
    escapeCitationBrackets,
    fixNestedMarkdownLinks,
    normalizeMathDelimiters,
    normalizeDisplayFences,
    resolveUnclosedDisplayMath,
    containsMathDelimiters,
    blankOutCodeAndMath,
} from "./utils";
import {ParagraphWithReferences} from "./inline/ParagraphWithReferences";
import ExternalLinkIcon from "../../../../assets/external-link.svg?react";
import ArrowsMaximizeIcon from "../../../../assets/arrows-maximize.svg?react";
import XIcon from "../../../../assets/x.svg?react";
import {useTranslation} from "react-i18next";
import {GPTMeta, GPTMetaContext} from "./MetaProvider";
import {useNavigate} from "react-router-dom";
import {useExperimentalLinkResolution} from "../../../../components/ExperimentalLink";
import {bffApi} from "../../bffApi";
import {noteTopicEntry} from "../../../content/topicEntryNote";
import {parseDibUrl, targetFromSource} from "../../citation-preview/useCitationParts";
import {useWorkIsland} from "../../work-island/WorkIslandContext";

// Custom components for the special tags

export const Thinking: React.FC<any> = ({children, ...props}) => {
    return (
        <Box
            p={"xs"}
            m={0}
            style={{borderLeft: "2px solid var(--mantine-color-gray-4)"}}
        >
            <Text fz="md" c="gray.6" m={0}>
                {processReferences(children)}
            </Text>
        </Box>
    );
};

export const Step: React.FC<any> = ({children, ...props}) => {
    return (
        <Box
            p={"xs"}
            style={{borderLeft: "2px solid var(--mantine-color-gray-4)"}}
        >
            <Text fz="md" c="gray.6" m={0}>
                {processReferences(children)}
            </Text>
        </Box>
    );
};

export const Count: React.FC<any> = ({children, ...props}) => {
    return (
        <div
            style={{
                backgroundColor: "#fff5e6",
                padding: "5px",
                borderRadius: "4px",
                color: "#ff6600",
                display: "none",
            }}
        >
            {children}
        </div>
    );
};

export const Reflection: React.FC<any> = ({children, ...props}) => {
    return (
        <Box
            p={"xs"}
            style={{borderLeft: "2px solid var(--mantine-color-gray-4)"}}
        >
            {processReferences(children)}
        </Box>
    );
};

export const Answer: React.FC<any> = ({children, ...props}) => {
    return <div>{processReferences(children)}</div>;
};

export const Reward: React.FC<any> = ({children, ...props}) => {
    return (
        <div
            style={{
                backgroundColor: "#f0f8ff",
                padding: "5px",
                borderRadius: "4px",
                fontStyle: "italic",
                display: "none",
            }}
        >
            {children}
        </div>
    );
};

/** Scheme for an inline company reference: `[Sticos AS](company:934228391)`. */
const COMPANY_URL_PREFIX = "company:";

/** react-markdown blanks any URL scheme it does not recognize. The
 *  "company:" scheme must survive untouched; every other URL still goes
 *  through the default sanitizer. */
const urlTransform = (url: string): string =>
    url.startsWith(COMPANY_URL_PREFIX) ? url : defaultUrlTransform(url);

// Custom link component for app.dib.no URLs, and for an inline company reference.
const DibAppLink: React.FC<{href: string; children: React.ReactNode}> = ({
    href,
    children,
}) => {
    const context = useContext(GPTMetaContext);
    const navigate = useNavigate();
    const {effectiveHref, internalPath} = useExperimentalLinkResolution(href);
    const {request: island, openIsland, mode: islandMode} = useWorkIsland();
    const linkTarget = parseDibUrl(internalPath);
    const openTopicId = targetFromSource(island?.source ?? null)?.resourceId;
    const navigatesOpenDocument = !!linkTarget.topicId &&
        linkTarget.topicId.toLowerCase() === openTopicId?.toLowerCase();
    const [trackReference] = bffApi.endpoints.trackReference.useMutation();

    if (href.startsWith(COMPANY_URL_PREFIX)) {
        const orgNr = href.slice(COMPANY_URL_PREFIX.length);
        return <CompanyReference orgNr={orgNr} name={children} />;
    }

    // A prose link that resolves to one of the answer's sources is a reference
    // open; tool links and plain links are not counted here.
    const matchedSource = useMemo(
        () =>
            context?.sources?.find((s) => href.includes(s.url)) ??
            context?.consideredSources?.find((s) => href.includes(s.url)),
        [context?.sources, context?.consideredSources, href]
    );

    const trackOpen = () => {
        if (!matchedSource) return;
        noteTopicEntry(targetFromSource(matchedSource)?.resourceId, "citation");
        trackReference({
            action: "opened",
            referenceKey: matchedSource.id || matchedSource.url,
            surface: "inline",
            position: matchedSource.index + 1,
            sourceType: matchedSource.type,
        });
    };
    const isDibAppLink =
        effectiveHref?.includes("app.dib.no") ||
        effectiveHref?.startsWith("/dibappapi/");

    const toolTitle = useMemo(() => {
        if (
            context?.relatedTools === undefined ||
            context?.relatedTools.length === 0
        ) {
            return undefined;
        }
        for (const tool of context?.relatedTools) {
            if (tool.url === href) {
                return tool.title;
            }
        }
        return undefined;
    }, [context?.relatedTools]);

    const usedSourceTitle = useMemo(() => {
        if (context?.sources === undefined || context?.sources.length === 0) {
            return undefined;
        }
        for (const source of context?.sources) {
            if (href.includes(source.url)) {
                return source.name;
            }
        }
        return undefined;
    }, [context?.sources]);

    const consideredSourceTitle = useMemo(() => {
        if (
            context?.consideredSources === undefined ||
            context?.consideredSources.length === 0
        ) {
            return undefined;
        }
        for (const source of context?.consideredSources) {
            if (href.includes(source.url)) {
                return source.name;
            }
        }
        return undefined;
    }, [context?.sources]);

    // Differentiate between plain links without title and titled markdown links
    // Plain links will be an array with href at index 0
    const altTitle =
        Array.isArray(children) && children.length > 0 && href === children[0]
            ? "app.dib"
            : children;

    const displayTitle =
        toolTitle || usedSourceTitle || consideredSourceTitle || altTitle;

    // Keep the display of this anchor at its default value, inline. inline-flex and
    // inline-block make the anchor a single box that cannot break across lines. A long
    // title then moves to the next line as a whole instead of wrapping.
    if ((isDibAppLink || navigatesOpenDocument) && internalPath) {
        return (
            <Anchor
                href={internalPath}
                onClick={(e: React.MouseEvent) => {
                    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                    e.preventDefault();
                    trackOpen();
                    if (navigatesOpenDocument && island) {
                        openIsland({kind: "topic", initialMode: islandMode, source: {
                            ...island.source, url: internalPath,
                            segmentId: linkTarget.segmentId ?? "", segmentName: "",
                        }});
                    } else {
                        navigate(internalPath, {viewTransition: true});
                    }
                }}
            >
                {navigatesOpenDocument ? children : displayTitle}
            </Anchor>
        );
    }

    // Keep the display of this anchor at its default value, inline. inline-flex and
    // inline-block make the anchor a single box that cannot break across lines. A long
    // title then moves to the next line as a whole instead of wrapping. flex makes the
    // anchor block-level, which puts the link on a line of its own. vertical-align
    // aligns the icon with the text.
    if (isDibAppLink) {
        return (
            <Anchor
                href={effectiveHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={trackOpen}
            >
                {displayTitle}
                <ExternalLinkIcon
                    width={16}
                    height={16}
                    style={{
                        verticalAlign: "text-bottom",
                        marginLeft: "4px",
                    }}
                />
            </Anchor>
        );
    }

    return (
        <Anchor
            href={effectiveHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={trackOpen}
        >
            {children}
        </Anchor>
    );
};

type Props = {
    text: string;
    meta: GPTMeta;
    /**
     * The time in milliseconds between each update
     */
    delta?: number;
    className?: string;
    hideCitations?: boolean;
    // The thoughts/progress panel sets this so a chart block never renders
    // there, streaming or not, only the final answer shows charts.
    hideCharts?: boolean;
    options?: {
        includeCustomComponents?: {
            thinking?: boolean;
            step?: boolean;
            count?: boolean;
            reflection?: boolean;
            answer?: boolean;
            reward?: boolean;
            code?: boolean;
            references?: boolean;
        };
        chunkSize?: number;
    };
    onFinish?: () => void;
};

// A wrapped component generator used for most markdown elements that must have references processed.
const createWrappedComponent = (Component: React.ElementType) => {
    return ({node, children, ...props}: any) => {
        const processedChildren = processReferences(children);
        return <Component {...props}>{processedChildren}</Component>;
    };
};

// Custom table components using Mantine's Table for better styling
const CustomTable: React.FC<any> = ({node, children, ...props}) => {
    const [expanded, setExpanded] = useState(false);
    const {t} = useTranslation();

    const tableContent = (
        <Table
            striped
            highlightOnHover
            withTableBorder
            withColumnBorders
            className={classes.table}
            {...props}
        >
            {children}
        </Table>
    );

    return (
        <>
            <Box className={classes.tableWrapper}>
                {tableContent}
                <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="sm"
                    className={classes.expandButton}
                    onClick={() => setExpanded(true)}
                    title={t("table.expand")}
                >
                    <ArrowsMaximizeIcon width={14} height={14} />
                </ActionIcon>
            </Box>

            <Modal
                opened={expanded}
                onClose={() => setExpanded(false)}
                size="xl"
                fullScreen
                classNames={{
                    body: classes.modalBody,
                    content: classes.modalContent,
                }}
                withCloseButton={false}
            >
                <Box
                    className={classes.expandedTableContainer}
                    onClick={() => setExpanded(false)}
                >
                    <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="md"
                        className={classes.closeButton}
                        onClick={() => setExpanded(false)}
                        title={t("table.close")}
                    >
                        <XIcon width={18} height={18} />
                    </ActionIcon>
                    <Box
                        className={classes.expandedTableScroll}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Table
                            striped
                            highlightOnHover
                            withTableBorder
                            withColumnBorders
                            className={classes.expandedTable}
                            {...props}
                        >
                            {children}
                        </Table>
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

const CustomThead: React.FC<any> = ({node, children, ...props}) => {
    return <Table.Thead {...props}>{children}</Table.Thead>;
};

const CustomTbody: React.FC<any> = ({node, children, ...props}) => {
    return <Table.Tbody {...props}>{children}</Table.Tbody>;
};

const CustomTr: React.FC<any> = ({node, children, ...props}) => {
    return <Table.Tr {...props}>{children}</Table.Tr>;
};

const CustomTh: React.FC<any> = ({node, children, ...props}) => {
    const processedChildren = processReferences(children);
    return <Table.Th {...props}>{processedChildren}</Table.Th>;
};

const CustomTd: React.FC<any> = ({node, children, ...props}) => {
    const processedChildren = processReferences(children);
    return <Table.Td {...props}>{processedChildren}</Table.Td>;
};

/**
 * CustomParagraph checks if its sole child is one of our custom elements.
 * If so, it returns that element directly (so that it isn't wrapped in <p></p>).
 * Otherwise, it falls back to a normal paragraph.
 */
const CustomParagraph: React.FC<any> = ({node, children, ...props}) => {
    const meta = useContext(GPTMetaContext);
    const childArray = React.Children.toArray(children);

    // A paragraph that holds nothing but a chart's JSON, with no fence around it.
    // Strict on purpose: only a whole paragraph of valid chart JSON qualifies.
    if (childArray.length === 1 && typeof childArray[0] === "string") {
        const trimmed = childArray[0].trim();
        if (trimmed.startsWith("{")) {
            const chart = isChartNode(trimmed);
            if (chart) {
                return meta?.hideCharts ? null : <ChartBlock chart={chart} />;
            }
            const compare = isCompareNode(trimmed);
            if (compare) {
                return meta?.hideCharts ? null : <CompareBlock compare={compare} />;
            }

            let parsed: unknown;
            try {
                parsed = JSON.parse(trimmed);
            } catch {
                parsed = undefined;
            }
            // JSON.parse never yields undefined for a real value, so this only
            // holds while the paragraph's own JSON has not closed yet.
            const stillParsing = parsed === undefined;

            // Show the placeholder only while this paragraph's own JSON has not
            // closed yet. Once it parses, even to something invalid, it is done:
            // it never waits on the rest of the answer to keep streaming.
            if (!meta?.hideCharts && meta?.isStreaming && stillParsing && looksLikeChartFence(null, trimmed)) {
                return <ChartSkeleton />;
            }
            if (!meta?.hideCharts && meta?.isStreaming && stillParsing && looksLikeCompareJson(trimmed)) {
                return <CompareSkeleton />;
            }

            const hasStringType =
                typeof parsed === "object" &&
                parsed !== null &&
                typeof (parsed as {type?: unknown}).type === "string";

            // A structured block (a known type that failed validation, an unknown type,
            // or one still streaming in) never falls back to the raw JSON paragraph.
            if (hasStringType) {
                const type = (parsed as {type?: unknown}).type;
                if (type === "chart") return meta?.hideCharts ? null : <ChartUnavailable />;
                if (type === "compare") return meta?.hideCharts ? null : <CompareUnavailable />;
                return null;
            }
            if (meta?.isStreaming && stillParsing) {
                return null;
            }
        }
    }

    // If the paragraph contains exactly one child AND that child is one of our custom tags,
    // then return it directly
    if (childArray.length === 1 && React.isValidElement(childArray[0])) {
        const element = childArray[0];
        const type = element.type;

        // Check against both string types and our imported component references.
        if (
            type === "thinking" ||
            type === Thinking ||
            type === "step" ||
            type === Step ||
            type === "count" ||
            type === Count ||
            type === "reflection" ||
            type === Reflection ||
            type === "answer" ||
            type === Answer ||
            type === "reward" ||
            type === Reward ||
            type === DibAppLink
        ) {
            return <>{element}</>;
        }
    }

    // Otherwise, render a normal <p> with processed references.
    return <ParagraphWithReferences>{children}</ParagraphWithReferences>;
};

const Nada = () => <></>;

/** Reads the plain text out of a fenced code block's rendered children. */
const extractCodeText = (node: React.ReactNode): string => {
    if (typeof node === "string") {
        return node;
    }
    if (Array.isArray(node)) {
        return node.map(extractCodeText).join("");
    }
    if (React.isValidElement(node)) {
        return extractCodeText((node.props as {children?: React.ReactNode}).children);
    }
    return "";
};

// Reads the fence's info string language, e.g. "chart" from ```chart. Looks past the
// rendered <code> element's className, the only place react-markdown puts it.
const FENCE_LANGUAGE = /language-(\S+)/;
const extractFenceLanguage = (node: React.ReactNode): string | null => {
    if (React.isValidElement(node)) {
        const className = (node.props as {className?: string}).className ?? "";
        const match = FENCE_LANGUAGE.exec(className);
        if (match) {
            return match[1];
        }
        return extractFenceLanguage((node.props as {children?: React.ReactNode}).children);
    }
    if (Array.isArray(node)) {
        for (const child of node) {
            const lang = extractFenceLanguage(child);
            if (lang) return lang;
        }
    }
    return null;
};

/**
 * Renders a fenced code block. A block whose content is a chart, compare, reference or
 * filtered-content node renders that component instead of the fence, regardless of the
 * fence's language tag (models drop it, or use "json" instead of "chart").
 *
 * A structured block never falls back to showing its raw JSON: a "chart"/"compare"
 * language fence, or any fence whose body parses as a JSON object with a string "type"
 * field, is treated as structured. Invalid chart/compare payloads show an unavailable
 * message, while unknown structured types stay hidden; only real code (no "type" field,
 * not a chart or compare fence) falls back to the plain block below.
 *
 * A chart or compare block still streaming in (incomplete JSON that reads like one, or
 * the language tag already set) shows a placeholder instead of nothing, unless
 * meta.hideCharts is set (the thoughts/progress panel), where neither ever renders.
 */
const CodeFence: React.FC<any> = ({children, node, ...props}) => {
    const meta = useContext(GPTMetaContext);
    const text = extractCodeText(children);
    const trimmed = text.trim();
    const lang = extractFenceLanguage(children);

    if (lang === "stats" || lang === "details") {
        const resolved = parseResolvedInfoBlock(trimmed);
        if (resolved && resolved.type === lang) {
            return meta?.hideCharts ? null : lang === "stats" ? <StatsBlock stats={resolved} /> : <DetailsBlock details={resolved} />;
        }
        let stillParsing = false;
        try { JSON.parse(trimmed); } catch { stillParsing = true; }
        if (meta?.hideCharts) return null;
        if (meta?.isStreaming && stillParsing) {
            return lang === "stats" ? <StatsSkeleton /> : <DetailsSkeleton />;
        }
        return lang === "stats" ? <StatsUnavailable /> : <DetailsUnavailable />;
    }

    if (lang === "email") {
        return meta?.hideCharts ? null : <EmailBlock text={trimmed} />;
    }

    if (lang === "resources") {
        return meta?.hideCharts ? null : <ResourcesBlock text={trimmed} />;
    }

    // The fence language alone decides intent: inside a "chart" fence, a "type"
    // field is optional and any value in it is ignored.
    const chart = isChartNode(trimmed, {ignoreType: lang === "chart"});
    if (chart) {
        return meta?.hideCharts ? null : <ChartBlock chart={chart} />;
    }

    // Same rule for a "compare" fence.
    const compare = isCompareNode(trimmed, {ignoreType: lang === "compare"});
    if (compare) {
        return meta?.hideCharts ? null : <CompareBlock compare={compare} />;
    }

    let parsed: unknown;
    try {
        parsed = JSON.parse(trimmed);
    } catch {
        parsed = undefined;
    }
    // JSON.parse never yields undefined for a real value, so this only holds
    // while the fence's own body is still incomplete.
    const stillParsing = parsed === undefined;
    const hasStringType =
        typeof parsed === "object" &&
        parsed !== null &&
        typeof (parsed as {type?: unknown}).type === "string";

    // renderNode only understands a real JSON payload (references, filtered
    // content). A plain code fence whose text merely contains a matching word,
    // such as a SQL comment mentioning "references", must never reach it.
    if (!stillParsing && typeof parsed === "object" && parsed !== null) {
        const rendered = renderNode(text);
        if (rendered) {
            return rendered;
        }
    }

    // Show the placeholder only while this block's own JSON has not closed yet.
    // Once it parses, even to something invalid, the block is done: it never
    // waits on the rest of the answer to keep streaming.
    if (!meta?.hideCharts && meta?.isStreaming && stillParsing) {
        if (looksLikeChartFence(lang, trimmed)) {
            return <ChartSkeleton />;
        }
        if (looksLikeCompareFence(lang)) {
            return <CompareSkeleton />;
        }
    }

    const parsedType =
        typeof parsed === "object" && parsed !== null
            ? (parsed as {type?: unknown}).type
            : undefined;
    if (lang === "chart" || parsedType === "chart") {
        return meta?.hideCharts ? null : <ChartUnavailable />;
    }
    if (lang === "compare" || parsedType === "compare") {
        return meta?.hideCharts ? null : <CompareUnavailable />;
    }

    if (hasStringType || (meta?.isStreaming && trimmed.startsWith("{"))) {
        return null;
    }

    return <pre {...props}>{children}</pre>;
};

// KaTeX and its stylesheet add about 90 KB. They stay out of the initial bundle and load
// only for an answer that contains math. The promise below keeps the load to one.
const importMathPlugins = () =>
    Promise.all([
        import("remark-math"),
        import("rehype-katex"),
        import("katex/dist/katex.min.css"),
    ]).then(([remarkMath, rehypeKatex]) => ({
        remark: remarkMath.default,
        rehype: rehypeKatex.default,
    }));

type MathPlugins = Awaited<ReturnType<typeof importMathPlugins>>;

let mathPluginsRequest: Promise<MathPlugins> | undefined;

/**
 * Starts the shared load, or returns the load that is in progress. A failed load is not
 * kept. The next answer with math starts a new load.
 */
const loadMathPlugins = (): Promise<MathPlugins> => {
    mathPluginsRequest ??= importMathPlugins().catch((error) => {
        mathPluginsRequest = undefined;
        throw error;
    });
    return mathPluginsRequest;
};

/**
 * Returns the math plugins when the text contains math and the load is complete. Returns
 * undefined before that. Without the plugins the math stays plain text.
 */
const useMathPlugins = (text: string): MathPlugins | undefined => {
    const [plugins, setPlugins] = useState<MathPlugins>();
    const hasMath = containsMathDelimiters(text);

    useEffect(() => {
        if (!hasMath) {
            return;
        }
        void loadMathPlugins().then(setPlugins, (error) => {
            console.error("Could not load KaTeX, math stays plain text:", error);
        });
    }, [hasMath]);

    return plugins;
};

// The HTML parser in rehype-raw opens a tag only when a letter, "/", "!", or "?" follows "<".
// The parser reads "<?" as a comment that includes all text up to the next ">".
const TAG_OPENER = /<[A-Za-z/!?]/;
// A trailing "<" in a stream can still become the start of a tag.
const TAG_OPENER_OR_END = /<([A-Za-z/!?]|$)/;

/**
 * Removes the text from a "<" that opens a tag with no ">" after it. A trailing "<" can still
 * become a tag while the stream runs or the typewriter types. It stays out of the text until the
 * text is the final text of a finished answer.
 */
const stripUnclosedCustomTags = (text: string, isFinal: boolean): string => {
    // Code and formulas hold "<" and ">" characters that open and close no tag.
    const searchable = blankOutCodeAndMath(text);
    // The last ">" closes every opener before it, even a ">" inside code or a formula.
    // The search reads the original text and not the blanked copy.
    const searchFrom = text.lastIndexOf(">") + 1;
    const opener = searchable
        .slice(searchFrom)
        .search(isFinal ? TAG_OPENER : TAG_OPENER_OR_END);
    return opener === -1 ? text : text.slice(0, searchFrom + opener);
};

// Keep this order. escapeCitationBrackets turns [1] into \[1\], and normalizeMathDelimiters
// reads \[1\] as math. normalizeDisplayFences runs before resolveUnclosedDisplayMath, so a
// fence that shares its line with the formula closes the block instead of escaping it.
const PREPARE_STEPS: ((text: string, isFinal: boolean) => string)[] = [
    stripUnclosedCustomTags,
    stripLCitations,
    fixNestedMarkdownLinks,
    normalizeDisplayFences,
];

const FINISH_STEPS: ((text: string) => string)[] = [
    normalizeMathDelimiters,
    escapeCitationBrackets,
];

const applyPrepareSteps = (text: string, isFinal: boolean): string =>
    PREPARE_STEPS.reduce((prepared, step) => step(prepared, isFinal), text);

/**
 * Prepares the typed part of an answer for markdown. The typewriter types a finished answer
 * from the start, so the typed part is shorter than the full text for a while. A display math
 * opener is escaped only when it is unclosed in the prepared full text too. The prepared full
 * text exists only for a finished answer.
 */
const prepareAnswerText = (
    typed: string,
    isComplete: boolean,
    fullText: string,
    preparedFullText: string | undefined
): string => {
    const isTypedToEnd = typed.length === fullText.length;
    const resolved = resolveUnclosedDisplayMath(
        applyPrepareSteps(typed, isComplete && isTypedToEnd),
        isComplete,
        isTypedToEnd ? undefined : preparedFullText
    );
    return FINISH_STEPS.reduce((prepared, step) => step(prepared), resolved);
};

// Main component that processes and renders markdown
export const GPTStreamParser: React.FC<Props> = ({
    text,
    meta,
    delta = 5,
    className,
    hideCitations,
    hideCharts,

    options = {
        chunkSize: 3,
        includeCustomComponents: {
            thinking: true,
            step: true,
            count: true,
            reflection: true,
            answer: true,
            reward: true,
            code: true,
            references: true,
        },
    },
    onFinish,
}) => {
    const displayText = useAnimatedText(text, {
        delta,
        chunkSize: 3,
        adaptive: true,
    });

    // Look at the full text, not the animated part, so the load starts while it streams.
    const mathPlugins = useMathPlugins(text);

    // The answer is complete as soon as the thread stops streaming. Do not wait for the
    // animation to catch up with the text: displayText lags behind for the whole typewriter
    // drain, and the answer would stay cut at an unclosed $$ or \[ until then. The length test
    // stays only in the onFinish effect below. A finished stream delivers no more text, but the
    // typewriter has not typed all of it. prepareAnswerText therefore escapes an opener only
    // when the full text leaves it unclosed.
    const isComplete = !meta?.isStreaming;
    const preparedFullText = useMemo(
        () => (isComplete ? applyPrepareSteps(text, true) : undefined),
        [isComplete, text]
    );

    // Debug
    /* const [debouncedText] = useDebouncedValue(text, 100);
    useEffect(() => {
        if (debouncedText) {
            console.log(`Base text before parsing: \n${debouncedText}`);
        }
    }, [debouncedText]); */

    useEffect(() => {
        if (!onFinish) {
            return;
        }

        if (text && displayText.length === text.length) {
            onFinish();
        }
    }, [onFinish, displayText, text]);

    const contextValue = useMemo(
        () => meta ? {...meta, answerText: text, ...(hideCharts ? {hideCharts} : {})} : undefined,
        [meta, hideCharts, text]
    );

    return (
        <Box className={`${classes.container} ${className} ${hideCitations ? classes.hideCitations : ""}`}>
            <GPTMetaContext.Provider value={contextValue}>
                <Markdown
                    remarkPlugins={[remarkGfm, ...(mathPlugins ? [mathPlugins.remark] : [])]}
                    urlTransform={urlTransform}
                    rehypePlugins={
                        // rehype-katex must come after rehype-raw, which rebuilds the tree.
                        [rehypeRaw, ...(mathPlugins ? [mathPlugins.rehype] : [])]
                    }
                    components={
                        {
                            li: createWrappedComponent("li"),
                            // Use our custom paragraph to "unwrap" custom blocks if detected.
                            p: CustomParagraph,
                            /* p: createWrappedComponent("p"), */
                            strong: createWrappedComponent("strong"),
                            em: createWrappedComponent("em"),
                            blockquote: createWrappedComponent("blockquote"),
                            // Custom table components with Mantine styling and reference processing
                            table: CustomTable,
                            thead: CustomThead,
                            tbody: CustomTbody,
                            tr: CustomTr,
                            th: CustomTh,
                            td: CustomTd,
                            // Fenced code blocks: chart/reference JSON renders in place, real code
                            // falls back to the default pre/code display.
                            pre: CodeFence,
                            // Map our custom HTML tags to the appropriate components.
                            references: options.includeCustomComponents
                                ?.references
                                ? CodeBlock
                                : Nada,
                            thinking: options.includeCustomComponents?.thinking
                                ? Thinking
                                : Nada,
                            step: options.includeCustomComponents?.step
                                ? Step
                                : Nada,
                            count: options.includeCustomComponents?.count
                                ? Count
                                : Nada,
                            reflection: options.includeCustomComponents
                                ?.reflection
                                ? Reflection
                                : Nada,
                            finalreflection: options.includeCustomComponents
                                ?.reflection
                                ? Reflection
                                : Nada,
                            answer: options.includeCustomComponents?.answer
                                ? Answer
                                : Nada,
                            reward: options.includeCustomComponents?.reward
                                ? Reward
                                : Nada,
                            a: DibAppLink,
                        } as Components
                    }
                >
                    {deduplicateEmailFences(prepareAnswerText(
                        displayText || text,
                        isComplete,
                        text,
                        preparedFullText
                    ))}
                </Markdown>
            </GPTMetaContext.Provider>
        </Box>
    );
};
